import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { setDialog } from '../../store/actions/dialog'
import {
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControl,
  FormGroup,
  FormControlLabel
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TinyMCE from './TinyMCE'
import Title from '../atoms/Title'
import DefaultModal from '../atoms/DefaultModal'
import UserInfoTable from '../molecules/UserInfoTable'
import CenteredSubmitButton from '../molecules/CenteredSubmitButton'
import { User } from '../../types/User'
import { Admin } from '../../types/Admin'
import store from '../../store'
import { setAuth } from '../../store/actions/auth'
import ValidationAndLimitLengthInput from '../atoms/ValidationAndLimitLengthInput'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    paddingBottom: 0,
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    width: '70vw',
    height: '90vh'
  }
}))

const CreateMessagePaper: React.FC<
  ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> & {
      users: User[]
      isOpen: boolean
      closeHandler: () => void
      appName: string
    }
> = ({ users, isOpen, closeHandler, setDialog, admin, setAuth, appName }) => {
  const [defaultNotificationMessage, setDefaultNotificationMessage] = React.useState('')
  const classes = useStyles({})
  const [inputValue, setInputValue] = useState({
    notification_message: '',
    subject: '',
    message: '',
    noreply_flg: false
  })
  const [showUserInfoTable, setShowUserInfoTable] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectUsers, setSelectUsers] = useState<User[]>([])
  const [validateState, setValidateState] = React.useState(false)
  const editorRef = useRef(null)

  useEffect(() => {
    setSelectUsers(users)
  }, [isOpen, users])

  useEffect(() => {
    setDefaultNotificationMessage(`${appName}に新着メッセージが届きました。`)
    setInputValue(prev => ({ ...prev, notification_message: defaultNotificationMessage }))
  }, [appName, defaultNotificationMessage])

  const fetchAdmin = async () => {
    const { data } = await axios.get<Admin>(`/admins/${admin.id}`)
    setAuth(admin.id, admin.token, admin.adminId, admin.password, data.name)
  }

  const handleInput = (key: string, value: string) => {
    setInputValue({ ...inputValue, [key]: value })
  }
  const checkInputValue = (inputValue: {
    notification_message: string
    subject: string
    message: string
    noreply_flg: boolean
  }) => {
    const { noreply_flg, ..._inputValue } = inputValue
    let countFalse = 0
    for (let v in _inputValue) {
      if (!_inputValue[v]) countFalse += 1
    }
    return countFalse === 0
  }
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      if (!inputValue.message) {
        alert('メッセージを入力してください。')
        return
      }
      if (selectUsers.length > 0) {
        setDialog(
          '確認',
          '対象者に対してメッセージを一斉送信します。対象者が多い場合、送信完了までに時間を要すことがあります。送信してよろしいですか？',
          async () => {
            setIsLoading(true)
            fetchAdmin()
            const { data } = await axios.post('/messages/send-to-user', {
              users: selectUsers.map(user => user.id),
              subject: inputValue.subject,
              message: inputValue.message,
              noreply_flg: inputValue.noreply_flg,
              sender: admin.name,
              sender_type: 'admin',
              notification_message: inputValue.notification_message
            })
            console.log(data)
            setIsLoading(false)
            handleClose()
          },
          () => {}
        )
      } else {
        setDialog(
          '',
          '送信対象のユーザーにチェックをいれてください。',
          async () => {},
          () => {},
          true
        )
      }
    } catch (error) {
      alert(error.message)
    }
  }
  const handleClose = () => {
    setInputValue({
      notification_message: defaultNotificationMessage,
      subject: '',
      message: '',
      noreply_flg: false
    })
    setShowUserInfoTable(false)
    setIsLoading(false)
    closeHandler()
  }
  const onClose = () => {
    if (inputValue.subject || inputValue.message) {
      setDialog(
        '確認',
        '入力内容は破棄されます。よろしいですか？',
        async () => {
          handleClose()
        },
        () => {}
      )
    } else {
      handleClose()
    }
  }

  return (
    <React.Fragment>
      <DefaultModal open={isOpen} onClose={onClose} loading={isLoading}>
        <Paper className={classes.paper}>
          <Box width="100%">
            <Title>メッセージを作成</Title>
            <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => handleSubmit(event)}>
              <Box my={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {users && (
                      <Typography variant="body1" paragraph>
                        {selectUsers.length}人のユーザーにメッセージを送信します。
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setShowUserInfoTable(!showUserInfoTable)}
                    >
                      送信対象の確認
                    </Button>
                    {showUserInfoTable && (
                      <UserInfoTable
                        users={users}
                        height={450}
                        checkUsers={selectUsers}
                        onDelete={true}
                        displayKatudouStatus={false}
                        displayKaiinEmail={true}
                        handleCheckUsers={selectedUsers => {
                          setSelectUsers(selectedUsers)
                        }}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" color="error">
                        ※は必須項目です。
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      件名
                      <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                        ※
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      value={inputValue.subject}
                      onChange={event => {
                        handleInput('subject', event.target.value)
                      }}
                      variant="outlined"
                      placeholder="件名を入力"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      プッシュ通知タイトル
                      <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                        ※
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ValidationAndLimitLengthInput
                      value={inputValue.notification_message}
                      label=""
                      required={false}
                      variant="outlined"
                      fullWidth
                      inputHandler={input => handleInput('notification_message', input)}
                      invalidStr={/["'<>&]/}
                      strLength={100}
                      validationErrorMessage="使用できない記号が含まれています。"
                      strLengthErrorMessage="文字数が100文字を超えています。文字数を調節してください。"
                      validationStateChange={isFullFilled => {
                        setValidateState(isFullFilled)
                      }}
                    />
                    <Box>
                      <Typography variant="caption" color="error" style={{ marginLeft: '12px' }}>
                        ※端末により40Byte以降の文字が表示されない場合もございます。
                      </Typography>
                      <br />
                      <Typography variant="caption" color="error" style={{ marginLeft: '24px' }}>
                        通知文字数が長くならないようご注意ください。
                      </Typography>
                      <br />
                      <Typography variant="caption" color="error" style={{ marginLeft: '12px' }}>
                        ※以下の記号類は、端末側で文字化けする可能性がありますので、使用しないでください。
                      </Typography>
                      <br />
                      <Typography variant="caption" color="error" style={{ marginLeft: '24px' }}>
                        「"」「&#39;」「&lt;」「&gt;」「&amp;」
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      メッセージ
                      <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                        ※
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TinyMCE
                      initialValue={inputValue.message}
                      editorRef={editorRef}
                      inputHandler={(tinyMCEContent: string) =>
                        handleInput('message', tinyMCEContent)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingTop: 20 }}>
                    <Typography variant="body1">
                      メッセージへの返信が不要な場合、「メッセージ受付停止」を選択してください。
                      <br />
                      ユーザーの返信フォームを非表示にします。
                    </Typography>
                    <FormControl component="fieldset" style={{ marginLeft: '1rem' }}>
                      <FormGroup aria-label="position" row>
                        <FormControlLabel
                          value="top"
                          control={
                            <Checkbox
                              checked={inputValue.noreply_flg}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                                checked: boolean
                              ) =>
                                setInputValue({
                                  ...inputValue,
                                  noreply_flg: checked
                                })
                              }
                            />
                          }
                          label="メッセージ受付停止"
                          labelPlacement="end"
                        />
                      </FormGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <CenteredSubmitButton
                      submittable={validateState && checkInputValue(inputValue)}
                      resettable
                      submitText="送信"
                      resetText="キャンセル"
                      submitHandler={() => {}}
                      resetHandler={onClose}
                      size="large"
                    />
                  </Grid>
                </Grid>
              </Box>
            </form>
          </Box>
        </Paper>
      </DefaultModal>
    </React.Fragment>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setDialog: (
      title: string,
      content: string,
      okHandler: () => void,
      ngHandler: () => void,
      noCancel?: boolean
    ) => dispatch(setDialog(title, content, okHandler, ngHandler, noCancel || false)),
    setAuth: (id: number, token: string, adminId: string, password: string, name: string) =>
      dispatch(setAuth(id, token, adminId, password, name))
  }
}

const mapStateToProps = (state: ReturnType<typeof store.getState>) => {
  return {
    admin: state.auth
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateMessagePaper)
